import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import TargetSelector from 'components/TargetSelector';
import 'assets/css/reports/Dashboard.css';

const DashboardOverview = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTarget, setSelectedTarget] = useState({ 
    id: 5, 
    name: 'www.gsa.gov', 
    displayName: 'GSA - General Services Administration' 
  });

  const loadDashboardData = useCallback(async (targetId) => {
    if (!targetId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      console.log(`[Dashboard] Loading data for target ${targetId}`);
      
      const response = await fetch(`http://localhost:4000/api/dashboard/overview?target=${targetId}`);
      
      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'API request failed');
      }
      
      console.log('[Dashboard] Data loaded successfully:', result.data);
      setDashboardData(result.data);
      
    } catch (err) {
      console.error('[Dashboard] Error loading data:', err);
      setError(err.message);
      setDashboardData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data when component mounts or target changes
  useEffect(() => {
    if (selectedTarget?.id) {
      loadDashboardData(selectedTarget.id);
    }
  }, [selectedTarget, loadDashboardData]);

  const handleTargetChange = useCallback((target) => {
    if (!target || target.id === selectedTarget?.id) return;
    console.log('[Dashboard] Target changed to:', target);
    setSelectedTarget(target);
  }, [selectedTarget]);

  const getStatusIcon = (passRate) => {
    if (passRate >= 90) return '✅';
    if (passRate >= 70) return '⚠️';
    return '❌';
  };

  const getStatusClass = (passRate) => {
    if (passRate >= 90) return 'status-excellent';
    if (passRate >= 70) return 'status-good';
    return 'status-poor';
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-header">
          <div className="header-left">
            <div className="shield-icon">🛡️</div>
            <div>
              <h1 className="dashboard-title">Federal Compliance Dashboard</h1>
              <p className="dashboard-subtitle">
                Loading compliance data for {selectedTarget?.displayName || 'selected target'}...
              </p>
            </div>
          </div>
        </div>
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Analyzing compliance reports...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-header">
          <div className="header-left">
            <div className="shield-icon">🛡️</div>
            <div>
              <h1 className="dashboard-title">Federal Compliance Dashboard</h1>
              <p className="dashboard-subtitle">Error loading compliance data</p>
            </div>
          </div>
        </div>
        <div className="error-container">
          <h3>❌ Error</h3>
          <p>{error}</p>
          <button 
            onClick={() => selectedTarget && loadDashboardData(selectedTarget.id)} 
            className="retry-button"
          >
            🔄 Retry
          </button>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-header">
          <div className="header-left">
            <div className="shield-icon">🛡️</div>
            <div>
              <h1 className="dashboard-title">Federal Compliance Dashboard</h1>
              <p className="dashboard-subtitle">No data available</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Target Selector */}
      <TargetSelector 
        selectedTarget={selectedTarget} 
        onTargetChange={handleTargetChange} 
      />
      
      {/* Header with Dynamic Target Info */}
      <div className="dashboard-header">
        <div className="header-left">
          <div className="shield-icon">🛡️</div>
          <div>
            <h1 className="dashboard-title">Federal Compliance Dashboard</h1>
            <p className="dashboard-subtitle">
              <strong>{selectedTarget?.displayName || 'Unknown Target'}</strong> Compliance Assessment
              <br />
              <small>Click on any framework to view detailed controls and remediation plans</small>
            </p>
          </div>
        </div>
        <div className="header-right">
          <div className="last-scan">Last Scan</div>
          <div className="scan-date">{dashboardData.lastScan}</div>
          <div className="scan-details">
            {dashboardData.totalScanners} scanners • {dashboardData.scanDuration} duration
          </div>
        </div>
      </div>

      {/* Real-time Metrics Grid */}
      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-label">Total Frameworks</div>
          <div className="metric-value frameworks">
            {dashboardData.metrics.totalFrameworks}
          </div>
          <div className="metric-icon">🛡️</div>
        </div>
        
        <div className="metric-card">
          <div className="metric-label">Controls Scanned</div>
          <div className="metric-value controls">
            {dashboardData.metrics.controlsScanned}
          </div>
          <div className="metric-icon">📊</div>
        </div>
        
        <div className="metric-card">
          <div className="metric-label">Avg Compliance</div>
          <div className={`metric-value compliance ${getStatusClass(dashboardData.metrics.avgCompliance)}`}>
            {dashboardData.metrics.avgCompliance}%
          </div>
          <div className="metric-icon">{getStatusIcon(dashboardData.metrics.avgCompliance)}</div>
        </div>
        
        <div className="metric-card">
          <div className="metric-label">Critical Issues</div>
          <div className="metric-value issues">
            {dashboardData.metrics.criticalIssues}
          </div>
          <div className="metric-icon">⚠️</div>
        </div>
      </div>

      {/* Dynamic Framework Cards */}
      <div className="frameworks-list">
        <div className="frameworks-header">
          <h2>Compliance Frameworks</h2>
          <p>Real-time compliance status for {selectedTarget?.displayName}</p>
        </div>
        
        {dashboardData.frameworks.map((framework) => (
          <div key={framework.id} className={`framework-card ${framework.id} ${getStatusClass(framework.passRate)}`}>
            <div className="framework-header">
              <div className="framework-info">
                <div className="framework-name">
                  <div className={`framework-status-dot ${getStatusClass(framework.passRate)}`}>
                    {getStatusIcon(framework.passRate)}
                  </div>
                  <span className="framework-title">{framework.name}</span>
                  <span className="framework-score">{framework.passRate}%</span>
                </div>
                <div className="framework-description">{framework.description}</div>
                <div className="framework-category">{framework.category}</div>
              </div>
            </div>

            <div className="framework-stats">
              <div className="stat-item">
                <div className={`stat-value ${getStatusClass(framework.passRate)}`}>
                  {framework.passRate}%
                </div>
                <div className="stat-label">Pass Rate</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{framework.controlsScanned}</div>
                <div className="stat-label">Controls</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{framework.totalViolations || 0}</div>
                <div className="stat-label">Violations</div>
              </div>
            </div>

            <div className="framework-progress">
              <div className="progress-bar">
                <div className="progress-segments">
                  <div 
                    className="progress-segment compliant" 
                    style={{width: `${(framework.compliant / (framework.compliant + framework.nonCompliant)) * 100}%`}}
                    title={`${framework.compliant} Compliant Controls`}
                  ></div>
                  <div 
                    className="progress-segment non-compliant" 
                    style={{width: `${(framework.nonCompliant / (framework.compliant + framework.nonCompliant)) * 100}%`}}
                    title={`${framework.nonCompliant} Non-Compliant Controls`}
                  ></div>
                </div>
              </div>
              
              <div className="controls-summary">
                <div className="control-count compliant">
                  <div className="count-dot compliant"></div>
                  <span className="count-number">{framework.compliant}</span>
                  <span className="count-label">Compliant</span>
                </div>
                <div className="control-count non-compliant">
                  <div className="count-dot non-compliant"></div>
                  <span className="count-number">{framework.nonCompliant}</span>
                  <span className="count-label">Non-Compliant</span>
                </div>
                {framework.critical > 0 && (
                  <div className="control-count critical">
                    <div className="count-dot critical"></div>
                    <span className="count-number">{framework.critical}</span>
                    <span className="count-label">Critical</span>
                  </div>
                )}
              </div>
            </div>

            <div className="framework-actions">
              <div className="controls-info">
                <span className="controls-scanned">
                  Controls: {framework.compliant + framework.nonCompliant} scanned
                </span>
                {framework.critical > 0 && (
                  <span className="critical-warning">
                    ⚠️ {framework.critical} critical issues
                  </span>
                )}
              </div>
              <Link 
                to={`/framework/${framework.id}?target=${selectedTarget.id}`} 
                className="view-details-btn"
              >
                View Detailed Controls & Remediation →
              </Link>
            </div>
          </div>
        ))}
      </div>

      {/* Target Comparison Footer */}
      <div className="dashboard-footer">
        <div className="footer-info">
          <h3>About This Assessment</h3>
          <p>
            This compliance dashboard shows real-time analysis results for{' '}
            <strong>{selectedTarget?.displayName}</strong> across {dashboardData.metrics.totalFrameworks} federal compliance frameworks.
            Data is generated from comprehensive security scans including accessibility, security, and design standards testing.
          </p>
          <div className="scan-summary">
            <span>📊 {dashboardData.metrics.controlsScanned} total controls assessed</span>
            <span>🕒 Last updated: {dashboardData.lastScan}</span>
            <span>🔄 Automated scanning every 24 hours</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;